// src/pages/NewPatientPage.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import QuestionStep from "../components/form/QuestionStep";
import Modal from "../components/Modal";
import api from "../api/axiosInstance";

/* ────────────────────────────────────────────────────────── */
/* 1.  Static questions                                      */
/* ────────────────────────────────────────────────────────── */
const BASE_QUESTIONS = [
  {
    id: "alias",
    title: "Patient ID (4 digits)",
    fieldType: "text", // keep as text to preserve leading zeros
    required: false, // optional: leave blank to auto-generate
    description:
      "Enter exactly 4 digits, or leave blank to auto-generate the next ID.",
  },
  {
    id: "dateOfBirth",
    title: "Date of Birth",
    fieldType: "date",
    required: true,
  },
  { id: "height", title: "Height (cm)", fieldType: "number", required: true },
  { id: "weight", title: "Weight (kg)", fieldType: "number", required: true },
  {
    id: "chestCircumference",
    title: "Chest Circumference (cm)",
    fieldType: "number",
    required: true,
    resourceUrl:
      "https://firebasestorage.googleapis.com/v0/b/pdx-web-v2.firebasestorage.app/o/metadata_form_assets%2Fchest_measurement.PNG?alt=media&token=8266715a-b1a8-4a64-a723-65b5eda7a092",
    description:
      "Measure at the topmost part of the chest – directly under the underarms and above the bust – keeping the tape level and snug.",
  },
  {
    id: "sexAtBirth",
    title: "Sex at Birth",
    fieldType: "select",
    required: true,
    options: [
      { value: "Female", label: "Female" },
      { value: "Male", label: "Male" },
    ],
  },
  {
    id: "ethnicity",
    title: "Ethnicity",
    fieldType: "select",
    required: true,
    options: [
      { value: "Caucasian", label: "Caucasian (White)" },
      { value: "Black", label: "Black" },
      { value: "North East Asian", label: "North-East Asian" },
      { value: "South East Asian", label: "South-East Asian" },
      { value: "Other", label: "Other / Mixed" },
    ],
  },
];

/* ────────────────────────────────────────────────────────── */
/* 2.  Component                                             */
/* ────────────────────────────────────────────────────────── */
export default function NewPatientPage({ userData }) {
  /* ---------- form state  -------------------------------- */
  const [form, setForm] = useState(() =>
    Object.fromEntries(
      [...BASE_QUESTIONS, { id: "groupIds" }].map((q) => [
        q.id,
        q.id === "groupIds" ? [] : "",
      ])
    )
  );

  /* ---------- groups / overlay state --------------------- */
  const [groupOptions, setGroupOptions] = useState(null);
  const [newGroupName, setNewGroupName] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  const nav = useNavigate();
  const { user } = useAuth();

  /* ---------- helpers ------------------------------------ */
  const handle = (id) => (e) =>
    setForm((p) => ({ ...p, [id]: e.target.value }));
  const focusNext = (idx, qs) => {
    const next = document.querySelector(`[name="${qs[idx + 1]?.id}"]`);
    next?.focus();
  };

  const handleAlias = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 4); // digits only, max 4
    setForm((p) => ({ ...p, alias: val }));
  };

  /* ---------- fetch groups once -------------------------- */
  useEffect(() => {
    if (!userData?.clinicId) return;
    api
      .get("/patients-api/groups", { params: { clinicId: userData.clinicId } })
      .then((res) => setGroupOptions(res.data))
      .catch(console.error);
  }, [userData]);

  /* ---------- build question list ------------------------ */
  const QUESTIONS = useMemo(() => {
    const qs = [...BASE_QUESTIONS];
    qs.splice(1, 0, {
      id: "groupIds",
      title: "Clinical Studies / Groups",
      fieldType: "multiselect",
      required: false,
      options: [
        ...(groupOptions ?? []).map((g) => ({
          value: g.group_id,
          label: g.name,
        })),
        { value: "__new", label: "Add new group +" },
      ],
    });
    return qs;
  }, [groupOptions]);

  /* multiselect handler – receives an array ------------------- */
  const onGroupChange = (ids) => {
    if (ids.includes("__new")) {
      /* strip sentinel before opening modal */
      setForm((p) => ({ ...p, groupIds: ids.filter((x) => x !== "__new") }));
      setShowCreate(true);
    } else {
      setForm((p) => ({ ...p, groupIds: ids }));
    }
  };

  /* ---------- create-group flow -------------------------- */
  const submitNewGroup = async () => {
    const name = newGroupName.trim();
    if (!name) return;

    try {
      const { data: g } = await api.post("/patients-api/groups", {
        clinicId: userData.clinicId,
        name,
      });
      setGroupOptions((prev) => [
        ...prev,
        { group_id: g.groupId, name: g.name },
      ]);
      setForm((p) => ({ ...p, groupIds: [...p.groupIds, g.groupId] }));
      setNewGroupName("");
      setShowCreate(false);
    } catch (err) {
      const msg =
        err.response?.data?.error || err.response?.statusText || err.message;
      alert(msg);
    }
  };

  /* ---------- submit ------------------------------------- */
  const submit = async (e) => {
    e.preventDefault();

    if (!userData?.clinicId) return alert("Missing clinic ID");

    // simple client validation for manual entry
    if (form.alias && form.alias.length !== 4) {
      return alert("Patient ID must be exactly 4 digits");
    }

    const { groupIds, alias, ...rest } = form;
    const payload = {
      clinicId: userData.clinicId,
      groupIds,
      createdByUid: user.uid,
      data: rest,
      digits: 4, // ensures backend generates 4-digit IDs if alias is blank
      ...(alias ? { alias } : {}),
    };

    try {
      const { data } = await api.post("/patients-api/create-patient", payload);
      console.log("patient created data", data);
      nav(`/patients/invite/${data.patientId}`, { state: data });
    } catch (err) {
      const msg =
        err.response?.data?.error || err.response?.statusText || err.message;
      alert(msg);
    }
  };

  /* ---------- UI ----------------------------------------- */
  return (
    <form onSubmit={submit} className="w-full max-w-lg p-4 flex flex-col gap-6">
      <h1 className="title-text">Add patient</h1>

      {QUESTIONS.map((q, idx) => (
        <QuestionStep
          key={q.id}
          question={q}
          value={form[q.id]}
          onChange={
            q.id === "groupIds"
              ? onGroupChange
              : q.id === "alias"
              ? handleAlias
              : handle(q.id)
          }
          onEnter={() => focusNext(idx, QUESTIONS)}
          autoFocus={idx === 0} // focus alias first
        />
      ))}

      <button className="primary-button self-start">Create Patient</button>

      {/* ───── overlay ───── */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)}>
        <h2 className="subtitle">New study / group</h2>
        <input
          className="custom-input w-full"
          placeholder="Group name"
          value={newGroupName}
          onChange={(e) => setNewGroupName(e.target.value)}
        />
        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            className="outlined-button"
            onClick={() => setShowCreate(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="primary-button"
            onClick={submitNewGroup}
          >
            Save
          </button>
        </div>
      </Modal>
    </form>
  );
}
